import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ActivitySuggestion } from "@/hooks/use-activity-suggestions";
import { useUpdateActivitySuggestion } from "@/hooks/use-activity-suggestions";
import { formatDate } from "@/lib/format";
import { ActivitySuggestionForm } from "./ActivitySuggestionForm";

interface ActivitySuggestionEditDialogProps {
  suggestion: ActivitySuggestion | null;
  onOpenChange: (open: boolean) => void;
}

function entrySelectedLabel(suggestion: ActivitySuggestion): string | undefined {
  if (!suggestion.entryId || !suggestion.entryStartDate) return undefined;
  return suggestion.entryStartDate === suggestion.entryEndDate
    ? formatDate(suggestion.entryStartDate)
    : `${formatDate(suggestion.entryStartDate)} – ${formatDate(suggestion.entryEndDate)}`;
}

/** Edit dialog — wraps the shared ActivitySuggestionForm (mirrors PtoEntryEditDialog.tsx). */
export function ActivitySuggestionEditDialog({
  suggestion,
  onOpenChange,
}: ActivitySuggestionEditDialogProps) {
  const updateSuggestion = useUpdateActivitySuggestion(suggestion?.id ?? "");

  return (
    <Dialog open={suggestion !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit suggestion</DialogTitle>
          <DialogDescription>Update this idea.</DialogDescription>
        </DialogHeader>
        {suggestion && (
          <ActivitySuggestionForm
            defaultValues={{
              title: suggestion.title,
              note: suggestion.note,
              entryId: suggestion.entryId,
              entrySelectedLabel: entrySelectedLabel(suggestion),
              category: suggestion.category,
            }}
            submitLabel="Save"
            isSaving={updateSuggestion.isPending}
            onCancel={() => onOpenChange(false)}
            onSubmit={(result) =>
              updateSuggestion.mutate(result, { onSuccess: () => onOpenChange(false) })
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
